import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bullseye,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  MenuToggle,
  Pagination,
  SearchInput,
  Spinner,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import {
  ActionsColumn,
  ExpandableRowContent,
  type IAction,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@patternfly/react-table';
import { EllipsisVIcon, FileInvoiceDollarIcon, SearchIcon } from '@patternfly/react-icons';
import { ADMIN_GPU_LIST_LIMIT, useAdminGpuInvoices } from '../../../hooks/useAdminVerdaServers';
import type { VerdaGpuInvoice } from '../../../types/models/Verda';
import { formatBareDateNumeric, formatMonthYear } from '../../../utils/dateFormat';
import { formatPrice } from '../../../utils/money';
import {
  invoiceCloseAvailability,
  invoiceResolvability,
  invoiceStatusColor,
  matchesInvoiceSearch,
} from './verdaGpuServers';
import type { ActionAvailability } from './verdaGpuServers';
import { NoSearchResults, StatusMultiSelect, useDebouncedValue } from './VerdaGpuParts';
import VerdaInvoiceActionModal, {
  type VerdaInvoiceAction,
  type VerdaInvoiceActionResult,
} from './VerdaInvoiceActionModals';
import { VerdaErrorAlert, VerdaRefreshButton } from './VerdaTabParts';

const INVOICE_STATUSES = ['claimed', 'issued', 'paid', 'parked', 'closed'];
const STATUS_OPTIONS = INVOICE_STATUSES.map((status) => ({ value: status, label: status }));
/** What needs staff attention first. */
const DEFAULT_STATUSES = ['claimed', 'issued', 'parked'];
const COLUMN_COUNT = 13;
const LAST_ERROR_PREVIEW = 40;

interface VerdaInvoicesTabProps {
  /** Defers the fetch until the staff gate has passed. */
  isStaff: boolean;
}

const actionItem = (title: string, availability: ActionAvailability, onClick: () => void): IAction =>
  availability.enabled
    ? { title, onClick }
    : { title, isAriaDisabled: true, tooltipProps: { content: availability.reason } };

/**
 * Admin › Verda › GPU invoices — the monthly consumption invoices, newest first
 * (`GET /admin/verda/invoices`, a database read), with the Link / Release / Close actions for the
 * ones that parked. `?invoice=<id>` (the findings link) fetches every status and narrows to that
 * invoice. Refreshed manually only.
 *
 * List-table decision (Bruno, 2026-09-28): client-side search by search marker (`<hostname> <period>`)
 * or client name, 400 ms debounce, top + bottom pagination at 25 per page. Client-side filtering is
 * correct because the read is bounded by the backend `limit` (the maximum, 500, is requested).
 */
const VerdaInvoicesTab: React.FunctionComponent<VerdaInvoicesTabProps> = ({ isStaff }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const invoiceFilter = searchParams.get('invoice');
  const [statuses, setStatuses] = useState<string[]>(DEFAULT_STATUSES);
  // The findings link names one invoice, whatever its status: read every status then.
  const { invoices, loading, error, refetch } = useAdminGpuInvoices(invoiceFilter !== null ? [] : statuses, isStaff);

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebouncedValue(searchValue);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set());
  const [pending, setPending] = useState<{ action: VerdaInvoiceAction; invoice: VerdaGpuInvoice } | null>(null);
  // Applied over the last read until the refetch that follows an action lands.
  const [updated, setUpdated] = useState<Record<number, VerdaGpuInvoice>>({});
  const [released, setReleased] = useState<ReadonlySet<number>>(() => new Set());

  useEffect(() => {
    setUpdated({});
    setReleased(new Set());
  }, [invoices]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statuses, invoiceFilter]);

  const rows = useMemo(
    () =>
      (invoices ?? []).filter((invoice) => !released.has(invoice.id)).map((invoice) => updated[invoice.id] ?? invoice),
    [invoices, updated, released],
  );

  const filtered = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    return rows.filter(
      (invoice) =>
        (invoiceFilter === null || String(invoice.id) === invoiceFilter) && matchesInvoiceSearch(invoice, query),
    );
  }, [rows, debouncedSearch, invoiceFilter]);

  const paginated = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page, perPage]);

  const clearInvoiceFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('invoice');
    setSearchParams(next, { replace: true });
  };

  const onActionDone = (result: VerdaInvoiceActionResult) => {
    if (result.kind === 'released') {
      setReleased((prev) => new Set(prev).add(result.id));
    } else {
      setUpdated((prev) => ({ ...prev, [result.invoice.id]: result.invoice }));
    }
    void refetch();
  };

  const toggleExpanded = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPage: number) => setPage(newPage);
  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const renderRow = (invoice: VerdaGpuInvoice, rowIndex: number) => {
    const now = Date.now();
    const resolvable = invoiceResolvability(invoice, now);
    const actions = [
      actionItem('Link a Facturier invoice', resolvable, () => setPending({ action: 'link', invoice })),
      actionItem('Release', resolvable, () => setPending({ action: 'release', invoice })),
      actionItem('Close', invoiceCloseAvailability(invoice), () => setPending({ action: 'close', invoice })),
    ];
    const lastError = invoice.last_error;
    const isLong = lastError !== null && lastError.length > LAST_ERROR_PREVIEW;
    const isExpanded = isLong && expanded.has(invoice.id);
    return (
      <Tbody key={invoice.id} isExpanded={isExpanded}>
        <Tr isContentExpanded={isExpanded}>
          <Td
            expand={
              isLong
                ? { rowIndex, isExpanded, onToggle: () => toggleExpanded(invoice.id), expandId: 'verda-gpu-invoice' }
                : undefined
            }
          />
          <Td dataLabel="ID">{invoice.id}</Td>
          <Td dataLabel="Period">{formatMonthYear(invoice.period)}</Td>
          <Td dataLabel="Server">
            <Link to={`/admin/verda/servers/${invoice.server.id}`}>
              <span className="pf-v6-u-font-family-monospace">
                {invoice.server.hostname ?? `#${invoice.server.id}`}
              </span>
            </Link>
          </Td>
          <Td dataLabel="Client">{invoice.client.name ?? `#${invoice.client.id}`}</Td>
          <Td dataLabel="Hours">{invoice.hours}</Td>
          <Td dataLabel="Amount TTC" modifier="nowrap">
            {formatPrice(invoice.amount_ttc)}
          </Td>
          <Td dataLabel="Paid TTC" modifier="nowrap">
            {formatPrice(invoice.paid_ttc)}
          </Td>
          <Td dataLabel="Status">
            <Label color={invoiceStatusColor(invoice.status)} isCompact>
              {invoice.status}
            </Label>
          </Td>
          <Td dataLabel="Invoice date" modifier="nowrap">
            {formatBareDateNumeric(invoice.invoice_date)}
          </Td>
          <Td dataLabel="Facturier">{invoice.facturier_numero ?? '—'}</Td>
          <Td dataLabel="Last error">
            {lastError === null ? '—' : isLong ? `${lastError.slice(0, LAST_ERROR_PREVIEW)}…` : lastError}
          </Td>
          <Td isActionCell>
            <ActionsColumn
              items={actions}
              actionsToggle={({ onToggle, isOpen, isDisabled, toggleRef }) => (
                <MenuToggle
                  ref={toggleRef}
                  aria-label={`Actions for GPU invoice #${invoice.id}`}
                  variant="plain"
                  onClick={onToggle}
                  isExpanded={isOpen}
                  isDisabled={isDisabled}
                  icon={<EllipsisVIcon />}
                />
              )}
            />
          </Td>
        </Tr>
        {isLong && (
          <Tr isExpanded={isExpanded}>
            <Td />
            <Td dataLabel="Last error" colSpan={COLUMN_COUNT - 1}>
              <ExpandableRowContent>{lastError}</ExpandableRowContent>
            </Td>
          </Tr>
        )}
      </Tbody>
    );
  };

  const renderBody = () => {
    if (error) {
      return <VerdaErrorAlert what="GPU invoices" message={error} />;
    }
    if (invoices === null) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading the GPU invoices" />
        </Bullseye>
      );
    }
    if (invoiceFilter !== null && !rows.some((invoice) => String(invoice.id) === invoiceFilter)) {
      return (
        <EmptyState titleText="Invoice not found" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            GPU invoice #{invoiceFilter} is not among the newest {ADMIN_GPU_LIST_LIMIT} invoices: it may have been
            released since the finding was raised.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={clearInvoiceFilter}>
                Show all invoices
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }
    if (rows.length === 0) {
      return (
        <EmptyState titleText="No GPU invoices" icon={FileInvoiceDollarIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>No GPU invoice matches the status filter.</EmptyStateBody>
        </EmptyState>
      );
    }
    if (filtered.length === 0) {
      return <NoSearchResults what="invoices" onClear={() => setSearchValue('')} />;
    }
    return (
      <>
        <Table aria-label="GPU invoices" variant="compact" isExpandable>
          <Thead>
            <Tr>
              <Th screenReaderText="Row expansion" />
              <Th>ID</Th>
              <Th>Period</Th>
              <Th>Server</Th>
              <Th>Client</Th>
              <Th>Hours</Th>
              <Th>Amount TTC</Th>
              <Th>Paid TTC</Th>
              <Th>Status</Th>
              <Th>Invoice date</Th>
              <Th>Facturier</Th>
              <Th>Last error</Th>
              <Th screenReaderText="Actions" />
            </Tr>
          </Thead>
          {paginated.map(renderRow)}
        </Table>
        <Pagination
          itemCount={filtered.length}
          perPage={perPage}
          page={page}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          variant="bottom"
        />
      </>
    );
  };

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by search marker or client..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={() => setSearchValue('')}
            />
          </ToolbarItem>
          {invoiceFilter === null ? (
            <ToolbarItem>
              <StatusMultiSelect
                options={STATUS_OPTIONS}
                selected={statuses}
                onChange={(next) => setStatuses(INVOICE_STATUSES.filter((status) => next.includes(status)))}
                ariaLabel="Filter by invoice status"
                label="Status"
              />
            </ToolbarItem>
          ) : (
            <ToolbarItem alignSelf="center">
              <Label color="blue" onClose={clearInvoiceFilter} closeBtnAriaLabel="Clear the invoice filter">
                Invoice #{invoiceFilter}
              </Label>
            </ToolbarItem>
          )}
          <ToolbarItem>
            <VerdaRefreshButton loading={loading} onRefresh={refetch} what="GPU invoices" />
          </ToolbarItem>
          {invoices !== null && !error && (
            <ToolbarItem alignSelf="center">
              <span className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
                {filtered.length} shown
                {invoices.length >= ADMIN_GPU_LIST_LIMIT && ` (the newest ${ADMIN_GPU_LIST_LIMIT} only)`}
              </span>
            </ToolbarItem>
          )}
          {invoices !== null && !error && filtered.length > 0 && (
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filtered.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
                isCompact
              />
            </ToolbarItem>
          )}
        </ToolbarContent>
      </Toolbar>
      {renderBody()}
      {pending !== null && (
        <VerdaInvoiceActionModal
          key={`${pending.action}-${pending.invoice.id}`}
          action={pending.action}
          invoice={pending.invoice}
          onClose={() => setPending(null)}
          onDone={onActionDone}
        />
      )}
    </>
  );
};

export default VerdaInvoicesTab;
